'use client';

import { useRef, type KeyboardEvent } from 'react';

import { CATALOGO_FUNCIONES_E1 } from '@/components/asistente/catalogoFuncionesE1';
import LeyendaIlustracion from '@/components/marketing/comun/LeyendaIlustracion';
import { cn } from '@/lib/utils';

import PoderesDemo from './PoderesDemo';
import { FUNCIONES_EN_ORDEN, GRUPOS_PODERES, siguienteIndice, type IdFuncionPoder } from './gruposPoderes';
import { TEXTOS_PODERES } from './textosPoderes';

// Escritorio de Poderes_Chat: una sola lista de pestañas con las 11 funciones, dibujada como la
// rejilla 2×2 de grupos del lienzo, y el panel con la demo. Nombre, descripción e icono salen del
// catálogo (D-22); el lienzo trae sus propios trazos de icono y una descripción de «Fecha real»
// más corta, y ninguna de las dos cosas se usa (C-4).

/** La receta de pestaña del lienzo; el móvil la reutiliza en sus botones de función. */
export function clasesFuncion(seleccionada: boolean): string {
    return cn(
        'flex w-full items-start gap-[10px] rounded-[14px] border-2 bg-surface-2 px-3 py-[9px] text-left text-ink',
        seleccionada ? 'border-ink shadow-ds-2' : 'border-surface-3 hover:border-ink/40',
    );
}

/** Icono, nombre y descripción de una función tal como los da el catálogo. */
export function ContenidoFuncion({ id, seleccionada }: { id: IdFuncionPoder; seleccionada: boolean }) {
    const ficha = CATALOGO_FUNCIONES_E1[id];
    const Icono = ficha.icono;
    return (
        <>
            <span
                className={cn(
                    'flex size-[30px] shrink-0 items-center justify-center rounded-[9px] text-primary',
                    seleccionada ? 'bg-accent-blue' : 'bg-tint-accent',
                )}
            >
                <Icono size={16} strokeWidth={1.5} aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-col gap-px">
                <strong data-parte="nombre" className="text-[14px] leading-[1.3]">
                    {ficha.nombre}
                </strong>
                <span data-parte="descripcion" className="text-[13px] leading-[1.4] text-on-surface-variant">
                    {ficha.descripcion}
                </span>
            </span>
        </>
    );
}

interface PropiedadesPestanas {
    idBase: string;
    seleccionada: IdFuncionPoder;
    alSeleccionar: (id: IdFuncionPoder) => void;
}

export function idPestana(idBase: string, id: IdFuncionPoder): string {
    return `${idBase}-pestana-${id}`;
}

const TECLAS_LISTA = ['ArrowDown', 'ArrowRight', 'ArrowUp', 'ArrowLeft', 'Home', 'End'] as const;

/**
 * Pestañas con tabindex itinerante y activación automática (UI-SPEC §Interaction 4): la tecla
 * elige la pestaña de destino y le pasa el foco. Solo se cancela el comportamiento por defecto de
 * las teclas que se atienden, para no robar Tab ni Enter. El móvil lo usa con izquierda/derecha.
 */
export function useTecladoPestanas<T extends string>(
    ids: readonly T[],
    alSeleccionar: (id: T) => void,
    teclas: readonly string[] = TECLAS_LISTA,
) {
    const refs = useRef<Partial<Record<T, HTMLButtonElement | null>>>({});
    const registrar = (id: T) => (nodo: HTMLButtonElement | null) => {
        refs.current[id] = nodo;
    };
    const alPulsarTecla = (actual: T, evento: KeyboardEvent<HTMLButtonElement>) => {
        if (!teclas.includes(evento.key)) return;
        evento.preventDefault();
        const destino = ids[siguienteIndice(ids.indexOf(actual), evento.key, ids.length)];
        alSeleccionar(destino);
        refs.current[destino]?.focus();
    };
    return { registrar, alPulsarTecla };
}

type Teclado = ReturnType<typeof useTecladoPestanas<IdFuncionPoder>>;

function Pestana({
    idBase,
    id,
    seleccionada,
    alSeleccionar,
    teclado,
}: PropiedadesPestanas & { id: IdFuncionPoder; teclado: Teclado }) {
    const activa = id === seleccionada;
    return (
        <button
            ref={teclado.registrar(id)}
            type="button"
            role="tab"
            onKeyDown={(evento) => teclado.alPulsarTecla(id, evento)}
            id={idPestana(idBase, id)}
            data-funcion={id}
            aria-selected={activa}
            aria-controls={`${idBase}-panel`}
            aria-describedby={`${idBase}-grupo-${CATALOGO_FUNCIONES_E1[id].columna}`}
            tabIndex={activa ? 0 : -1}
            onClick={() => alSeleccionar(id)}
            className={clasesFuncion(activa)}
        >
            <ContenidoFuncion id={id} seleccionada={activa} />
        </button>
    );
}

function ListaFunciones(props: PropiedadesPestanas) {
    const teclado = useTecladoPestanas(FUNCIONES_EN_ORDEN, props.alSeleccionar);
    return (
        <div
            role="tablist"
            aria-label={TEXTOS_PODERES.etiquetaFunciones}
            className="grid max-w-[780px] grid-cols-2 gap-x-5 gap-y-[22px]"
        >
            {GRUPOS_PODERES.map((grupo) => (
                <div key={grupo.columna} className="flex min-w-0 flex-col gap-[6px]">
                    <span
                        id={`${props.idBase}-grupo-${grupo.columna}`}
                        aria-hidden="true"
                        className="text-[12px] font-bold uppercase tracking-[0.1em] text-on-surface-variant"
                    >
                        {grupo.nombre}
                    </span>
                    {grupo.funciones.map((id) => (
                        <Pestana key={id} id={id} teclado={teclado} {...props} />
                    ))}
                </div>
            ))}
        </div>
    );
}

export default function PestanasPoderesEscritorio(props: PropiedadesPestanas) {
    const { idBase, seleccionada } = props;
    return (
        <div className="grid grid-cols-[minmax(0,780px)_460px] items-start justify-between gap-x-10">
            <ListaFunciones {...props} />
            <div
                role="tabpanel"
                id={`${idBase}-panel`}
                aria-labelledby={idPestana(idBase, seleccionada)}
                // La demo no tiene nada enfocable: sin tabIndex el panel no se alcanzaría con Tab (APG Tabs).
                tabIndex={0}
                className="flex flex-col gap-[10px]"
            >
                <PoderesDemo funcion={seleccionada} ancho={460} alto={560} />
                <LeyendaIlustracion />
            </div>
        </div>
    );
}
